<script lang="typescript">
    import { createEventDispatcher, onDestroy, onMount } from "svelte";
    import { slide } from "svelte/transition";

    export let id = "input";
    export let value: string | number;
    export let name = "name";
    export let placeholder = "My Input";
    export let disabled = false;

    export let label = "Input Label";
    export let bg = "var(--color-level-1)";
    export let type: "text" | "password" | "number" = "text";
    export let autocomplete: "on" | "off" = "off";
    export let width = "100%";

    export let validations: ((
        name: string,
        value: any
    ) => IValidationResult)[] = [];
    let validation: IValidationResult = { isValid: true, error: "" };

    const dispatch = createEventDispatcher();

    let focused = false;
    const onFocus = () => (focused = true);
    const onBlur = () => (focused = false);

    let el: HTMLInputElement;
    let form: HTMLFormElement | null;

    const dispatchValidation = () => {
        if (validations.length === 0 || !el) return;

        let newResult = { isValid: true, error: "" };
        // if we found any validation error and return the first one
        for (const validationFun of validations) {
            let result = validationFun(name, value);
            if (!result.isValid) {
                newResult = result;
                break;
            }
        }

        validation = newResult;

        sendValidation(validation);
    };

    const sendValidation = (
        validation: IValidationResult,
        fromParent = false
    ) => {
        const validationEvent = new CustomEvent("form-validation", {
            bubbles: true,
            detail: {
                name,
                validation,
            },
        });

        el.dispatchEvent(validationEvent);

        if (fromParent && form) {
            form.dispatchEvent(validationEvent);
        }
    };

    const handleInput = (e: any) => {
        // in here, you can switch on type and implement
        // whatever behaviour you need
        value = type.match(/^(number|range)$/)
            ? +e.target.value
            : e.target.value;

        dispatchValidation();
        dispatch("input", { value });
    };

    let iValue = value;

    $: {
        iValue = value;
        dispatchValidation();
    }

    onMount(() => {
        form = el.form;
        // post mount after form
        setTimeout(() => {
            dispatchValidation();
        }, 0);
    });

    onDestroy(() => {
        sendValidation({ isValid: true, error: "" }, true);
    });
</script>

<div class="input-area">
    <label
        style="background: {bg};"
        class:focused
        class="input-label"
        {disabled}
        for={name}>{label}</label
    >
    <input
        bind:this={el}
        {id}
        {name}
        style="background: {bg}; width: {width};"
        class:focused
        class="input"
        {type}
        {placeholder}
        on:input={handleInput}
        on:change
        {disabled}
        on:focus={onFocus}
        on:blur={onBlur}
        {autocomplete}
        {value}
    />
    {#if !validation.isValid}
        <label transition:slide class="error-label" for={name}
            >{validation.error}</label
        >
    {/if}
</div>

<style>
    /*  
    --color-level-0: #1e1e1e;
    --color-level-1: #2c2c2c;
    --color-level-2: #393939;
    --color-level-3: #595959;
    --color-level-4: #666666;
    --color-level-5: #808080;
    --color-level-6: #b7b7b7;
    --color-level-7: #efefef; 
    */
    :root {
        --input-border-color: var(--color-level-3, #595959);
        --input-color: var(--color-level-8, #b7b7b7);
        --input-active-color: var(--color-primary, #bb86fc);
        --input-disabled-color: var(--color-level-5, #595959);
    }

    .input-area {
        position: relative;
        /* height: 74px; */
        display: flex;
        /* align-items: center; */
        flex-grow: 1;
        flex-direction: column;
        padding: 8px 0;
    }
    .input-label {
        position: absolute;
        top: 2px;
        left: 10px;
        font-size: 12px;
        color: var(--input-color);
        padding: 0 6px;
        z-index: 1;
    }

    .error-label {
        color: var(--color-alert);
        /* position: absolute; */
        /* bottom: -10px; */
        font-size: 12px;
        /* left: 16px; */
        padding: 8px 16px 0px 16px;
    }
    .input {
        border-radius: 4px;
        border: 2px solid var(--input-color);
        height: 56px;
        width: 328px;
        font-size: 14px;
        background: transparent;
        color: var(--input-color);
        padding: 16px;
        outline: none !important;
    }
    .input:focus {
        border: 2px solid var(--input-active-color);
    }
    .focused {
        color: var(--input-active-color);
    }

    .input[disabled="true"] {
        border: 2px solid var(--input-disabled-color);
        color: var(--input-disabled-color);
    }
    .input-label[disabled="true"] {
        color: var(--input-disabled-color);
    }
</style>
